/*
 * Copyright (c) 2022. Dave van Rijn Development
 */

import './style/header.scss';

import React, {MouseEventHandler, useCallback, useEffect, useRef, useState} from 'react';
import {DVRHeader, HeaderItem} from '@dvrd/dvr-controls';
import {getImg} from "../../util/utils";
import {getHeaderItems} from "../../routes";
import {Carousel} from 'react-responsive-carousel';

interface Props {
    onClickLogo: MouseEventHandler;
    onClickItem: (item: HeaderItem) => MouseEventHandler;
}

const IMAGE_SIZES: number[] = [599, 959, 1279, 1920, 3024];

const getImageSizes = (): string => {
    const imageSizes: string[] = []
    // Skip last item since that is the fallback size
    for (let i = 0; i < IMAGE_SIZES.length - 2; i++) {
        const size = IMAGE_SIZES[i];
        imageSizes.push(`(max-width: ${size}px) ${size}px`)
    }
    return imageSizes.join(', ') + `, ${IMAGE_SIZES[IMAGE_SIZES.length - 1]}px`;
};

const getImageSources = (imgName: string): string => {
    const sources: string[] = [];
    for (const size of IMAGE_SIZES)
        sources.push(`${getImg(`${imgName}-x${size}`)} ${size}w`);
    return sources.join(', ');
};

const SLIDE_INTERVAL = 5000;

export default function Header(props: Props) {
    const {onClickItem, onClickLogo} = props;
    const [autoPlayHeader, setAutoPlayHeader] = useState(true);
    const header = useRef<DVRHeader>(null);
    const carouselContainer = useRef<HTMLDivElement>(null);
    const scrolling = useRef(false);

    function onLogoLoaded() {
        header.current?.setHeaderHeight();
    }

    function getLeftItem() {
        return (
            <div className='header-logo-container'>
                <img src={getImg('bowies_logo_excl-x200')} alt={'Bowie\'s'} className='header-logo'
                     onClick={onClickLogo} onLoad={onLogoLoaded}/>
            </div>
        );
    }

    const scrollListener = useCallback(() => {
        requestAnimationFrame(() => {
            if (!scrolling.current) {
                scrolling.current = true;
                window.setTimeout(() => {
                    scrolling.current = false;
                }, 20);
                const inView = carouselIsInView();
                if (!inView) setAutoPlayHeader(false);
                else if (inView) setAutoPlayHeader(true);
            }
        })
    }, [setAutoPlayHeader]);

    function carouselIsInView() {
        if (carouselContainer.current) {
            const box = carouselContainer.current.getBoundingClientRect();
            return box.bottom > 0;
        }
        return false;
    }

    function renderHeaderImages() {
        const imgNames: string[] = ['header/dogxygen-djuna', 'header/djuna-domino',
            'header/djuna-chef', 'header/bowies-header'];
        return (
            <div ref={carouselContainer}>
                <Carousel autoPlay={autoPlayHeader} infiniteLoop showArrows={false} showIndicators={false}
                          showStatus={false} showThumbs={false} interval={SLIDE_INTERVAL}>
                    {imgNames.map((imgName: string, index: number) => (
                        <img key={index} src={getImg(`${imgName}-x3024`)} alt="Bowie's"
                             srcSet={getImageSources(imgName)}
                             sizes={getImageSizes()} className='header-image'/>
                    ))}
                </Carousel>
            </div>
        )
    }

    useEffect(() => {
        document.addEventListener('scroll', scrollListener, {passive: true});

        return function () {
            document.removeEventListener('scroll', scrollListener);
        }
    }, []);

    return (
        <>
            <DVRHeader items={getHeaderItems()} id='app-header' leftMenuComp={getLeftItem()}
                       headerColor='#e9f6e8' headerLabelColor='#3a4044' containerClass='bowies-header' fix
                       ref={header} onClickItem={onClickItem}/>
            {renderHeaderImages()}
        </>
    )
}